import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { X, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { FORM_CONTROLS, RESPONSIVE, TYPOGRAPHY } from "@/design-system";
import { cn } from "@/lib/utils";
import { useLocalizations } from "@/hooks/useLocalizations";
import { LComponents } from "@/components/i18n";

export function EmailVerificationBanner() {
  const { user, resendConfirmationEmail } = useAuth();
  const { lCommon } = useLocalizations();
  const [isVisible, setIsVisible] = useState(false);
  const [emailVerified, setEmailVerified] = useState(true);
  const [resendingEmail, setResendingEmail] = useState(false);

  useEffect(() => {
    checkVerificationStatus();
  }, [user]);

  const checkVerificationStatus = () => {
    if (!user) {
      setIsVisible(false);
      return;
    }

    // Use user from context directly - no async call needed
    // This eliminates the race condition during HMR/domain switches
    const isVerified = user.email_confirmed_at != null;
    
    setEmailVerified(isVerified);

    // If email is verified, clear any dismissal data and hide banner
    if (isVerified) {
      localStorage.removeItem(`email-verification-dismissed-${user.id}`);
      setIsVisible(false);
      return;
    }

    // Check if user dismissed the banner and when
    const dismissedTimestamp = localStorage.getItem(`email-verification-dismissed-${user.id}`);
    
    if (dismissedTimestamp) {
      const dismissedTime = parseInt(dismissedTimestamp);
      const now = Date.now();
      const twentyFourHours = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      
      // Show banner again if 24 hours have passed since dismissal
      if (now - dismissedTime >= twentyFourHours) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    } else {
      // Never dismissed before, show banner
      setIsVisible(true);
    }
  };

  const handleDismiss = () => {
    if (user) {
      // Store the current timestamp when dismissed
      localStorage.setItem(`email-verification-dismissed-${user.id}`, Date.now().toString());
    }
    setIsVisible(false);
  };

  const handleResendVerification = async () => {
    if (!user?.email) return;
    
    setResendingEmail(true);
    try {
      const { error } = await resendConfirmationEmail(user.email);
      
      if (error) {
        toast.error(error.message || lCommon('toast.failed_to_resend_verification.error', { defaultValue: 'Failed to resend verification. Please try again.' }));
      } else {
        toast.success(lCommon('toast.verification_email_sent.success', { defaultValue: 'Verification email sent.' }));
      }
    } catch (error) {
      toast.error(lCommon('toast.unexpected.error', { defaultValue: 'Something went wrong.' }));
    } finally {
      setResendingEmail(false);
    }
  };

  if (!isVisible || emailVerified) {
    return null;
  }

  return (
    <div className="border-b border-warning/50">
      <div className="container px-4">
        <Alert variant="warning" className="border-0 bg-transparent py-3 px-0 my-0">
          <div className="flex items-start gap-3">
            <Mail className="h-4 w-4 shrink-0 mt-0.5" />
            <AlertDescription className="flex-1">
              <div className={RESPONSIVE.headerWithActionsGap3}>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-medium"><LComponents
                    id="email_verification.not_verified.description"
                    defaultValue='Your email address is not verified.' /></span>
                  <span className="text-sm"><LComponents
                    id="email_verification.check_inbox.description"
                    defaultValue='Please check your inbox.' /></span>
                  <span className={cn(TYPOGRAPHY.caption, RESPONSIVE.tabletUpInline)}><LComponents
                    id="email_verification.reminder.description"
                    defaultValue='Please verify your email address.' /></span>
                </div>
                
                <div className={cn(RESPONSIVE.flexRowCenterGap2, 'shrink-0')}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResendVerification}
                    disabled={resendingEmail}
                    className="h-8 flex-1 sm:flex-none"
                  >
                    {resendingEmail ? (
                      <>
                        <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                        <LComponents id="email_verification.sending" defaultValue='Sending Verification Email' />
                      </>
                    ) : (
                      <LComponents id="email_verification.resend" defaultValue='Resend Email' />
                    )}
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleDismiss}
                    className={cn(FORM_CONTROLS.button.iconTouch, "shrink-0")}
                    aria-label={lCommon('aria.dismiss_notification', { defaultValue: 'Dismiss notification' })}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </AlertDescription>
          </div>
        </Alert>
      </div>
    </div>
  );
}